import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";

import { FiltersPanel, Spinner } from "../../components/ui";
import { useCoreContext } from "../../context/global/CoreContext";
import {
  PROJECTS_HOME_ADD_PROJECT_LABEL,
  PROJECTS_HOME_DATE_RANGE_DEFAULTS,
  PROJECTS_HOME_DATE_RANGE_FILTER,
  PROJECTS_HOME_DEFAULT_VIEW,
  PROJECTS_HOME_DOCUMENT_TITLE,
  PROJECTS_HOME_FILTERS,
  PROJECTS_HOME_MULTI_SELECT_FILTERS,
  PROJECTS_HOME_SEARCH_PLACEHOLDER,
  PROJECTS_HOME_SORT_DEFAULTS,
  PROJECTS_HOME_SORT_FILTER,
  PROJECTS_HOME_SORT_OPTIONS,
  PROJECTS_HOME_VIEW_MODES,
  PROJECTS_HOME_VIEW_STORAGE_KEY,
} from "../../features/projects/constants";
import ProjectFormModal from "../../features/projects/shared/ProjectFormModal";
import { canManageProjects } from "../../features/projects/shared/projectPermissions";
import ProjectCard from "../../features/projects/projectsHome/ProjectCard";
import ProjectList from "../../features/projects/projectsHome/ProjectList";
import {
  buildProjectCustomerFilterOptions,
  buildProjectManagerFilterOptions,
  distributeProjectsIntoColumns,
  emptyProjectCardOpsSummary,
  normalizeProjectCardOpsSummary,
  projectMatchesCustomerFilter,
  projectMatchesDateRangeFilter,
  projectMatchesManagerFilter,
  sortProjectsForHomeCards,
} from "../../features/projects/projectsHome/projectHomeHelpers";
import ProjectsViewToggle from "../../features/projects/projectsHome/ProjectsViewToggle";
import useProjectsHomeCardColumnCount from "../../features/projects/projectsHome/useProjectsHomeCardColumnCount";
import {
  buildRfiCardSummary,
  buildTmCardSummary,
  emptyRfiCardSummary,
  emptyTmCardSummary,
  groupTicketsByProjectId,
} from "../../features/projects/shared/projectTicketSummaries";
import { isDayWithinJobActiveRange } from "../../features/planning/schedule/model/scheduleJobDateRange";
import {
  createProject,
  getProjectOpsSummaries,
  getProjects,
  updateProject,
} from "../../services/api/projects";
import { getRFIProjectSummaries } from "../../services/api/rfi";
import { getTMProjectSummaries } from "../../services/api/tmlog";
import { Plus, Search } from "../../components/icons/svg/outline";
import { getProjectAddressLabel } from "../../utils/business";
import { getTodayInPT } from "../../utils/datetime";

/**
 * Load concrete + dispatch labor brief summaries for visible projects.
 *
 * @param {Array<Object>} projects - Visible project rows.
 * @param {AbortSignal} signal - Abort signal for in-flight requests.
 * @returns {Promise<Map<string, ReturnType<typeof normalizeProjectCardOpsSummary>>>}
 */
async function loadOpsSummariesForProjects(projects, signal) {
  const summaries = new Map();
  if (!projects.length) {
    return summaries;
  }

  const jobIds = projects.map((project) => project.id);
  try {
    const payload = await getProjectOpsSummaries({ jobIds }, { signal });
    const rows = payload?.summaries || {};
    for (const project of projects) {
      const key = String(project.id);
      summaries.set(key, normalizeProjectCardOpsSummary(rows[key]));
    }
  } catch (requestError) {
    if (signal.aborted) {
      return summaries;
    }
    // Cards fall back to zeroed metrics, so report the failure loudly.
    console.error("Failed to load project ops summaries", requestError);
    Sentry.captureException(requestError, {
      tags: { component: "ProjectsHome" },
      extra: { jobCount: jobIds.length },
    });
    for (const project of projects) {
      summaries.set(String(project.id), emptyProjectCardOpsSummary());
    }
  }
  return summaries;
}

/**
 * Return the configured default value for a Projects Home filter.
 *
 * @param {string} key - Filter config key.
 * @returns {boolean}
 */
function getProjectHomeFilterDefault(key) {
  return Boolean(
    PROJECTS_HOME_FILTERS.find((filter) => filter.key === key)?.defaultValue
  );
}

/**
 * Read the persisted list/card view preference from localStorage.
 *
 * @returns {'list'|'card'}
 */
function readStoredProjectsHomeView() {
  try {
    const stored = localStorage.getItem(PROJECTS_HOME_VIEW_STORAGE_KEY);
    if (
      stored === PROJECTS_HOME_VIEW_MODES.LIST ||
      stored === PROJECTS_HOME_VIEW_MODES.CARD
    ) {
      return stored;
    }
  } catch {
    // Ignore private-mode / blocked storage access.
  }
  return PROJECTS_HOME_DEFAULT_VIEW;
}

/**
 * Filter projects by the current search query.
 *
 * @param {Array<Object>} projects - Project API rows.
 * @param {string} query - Search text.
 * @returns {Array<Object>}
 */
function filterProjects(projects, query) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return projects;
  }

  return projects.filter((project) => {
    const searchableText = [
      project.number,
      project.description,
      project.project_number,
      project.customer?.name,
      project.display_name,
      getProjectAddressLabel(project),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchableText.includes(normalizedQuery);
  });
}

/**
 * Projects module landing page backed by core JobNumber records.
 *
 * Loads the full project list once, then filters client-side (search, completed,
 * date range, customer, manager). Ticket summaries use slim T&M/RFI
 * project-summary endpoints, then group by project for card tiles.
 *
 * Module access and create/edit: manager/executive.
 *
 * @returns {JSX.Element}
 */
const ProjectsHome = () => {
  const { user } = useCoreContext();
  const canManage = canManageProjects(user);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCompletedProjects, setShowCompletedProjects] = useState(() =>
    getProjectHomeFilterDefault("showCompletedProjects")
  );
  const [showZeroMetrics, setShowZeroMetrics] = useState(() =>
    getProjectHomeFilterDefault("showZeroMetrics")
  );
  const [useFlexGridLayout, setUseFlexGridLayout] = useState(() =>
    getProjectHomeFilterDefault("useFlexGridLayout")
  );
  const [sortMode, setSortMode] = useState(PROJECTS_HOME_SORT_DEFAULTS.mode);
  const [reverseSort, setReverseSort] = useState(
    PROJECTS_HOME_SORT_DEFAULTS.reverse
  );
  const [filterDateStart, setFilterDateStart] = useState(
    PROJECTS_HOME_DATE_RANGE_DEFAULTS.start
  );
  const [filterDateEnd, setFilterDateEnd] = useState(
    PROJECTS_HOME_DATE_RANGE_DEFAULTS.end
  );
  const [filterCustomerIds, setFilterCustomerIds] = useState([]);
  const [filterManagerIds, setFilterManagerIds] = useState([]);
  const [viewMode, setViewMode] = useState(readStoredProjectsHomeView);
  const [editingProject, setEditingProject] = useState(null);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tmLogs, setTmLogs] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);
  const [opsSummariesByProjectId, setOpsSummariesByProjectId] = useState(
    () => new Map()
  );
  const [opsLoading, setOpsLoading] = useState(false);
  const cardColumnCount = useProjectsHomeCardColumnCount();
  const isCardView = viewMode === PROJECTS_HOME_VIEW_MODES.CARD;

  const isProjectModalOpen = isAddingProject || Boolean(editingProject);
  const projectModalMode = isAddingProject ? "add" : "edit";

  useEffect(() => {
    document.title = PROJECTS_HOME_DOCUMENT_TITLE;
  }, []);

  // Persist view mode so back navigation / remounts restore the same choice.
  useEffect(() => {
    try {
      localStorage.setItem(PROJECTS_HOME_VIEW_STORAGE_KEY, viewMode);
    } catch {
      // Ignore private-mode / blocked storage access.
    }
  }, [viewMode]);

  const loadProjects = useCallback(() => {
    setIsLoading(true);
    setError(null);
    // Always load the full set so the "of N" count includes hidden/completed projects.
    getProjects({ activeOnly: false })
      .then(setProjects)
      .catch((requestError) => {
        console.error("Failed to load projects", requestError);
        setError("Projects could not be loaded. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, []);

  const loadTicketSummaries = useCallback(() => {
    setTicketsLoading(true);
    setTicketsError(null);
    // Slim project-summary endpoints (not full T&M/RFI catalogs).
    Promise.all([getTMProjectSummaries(), getRFIProjectSummaries()])
      .then(([tmRows, rfiRows]) => {
        setTmLogs(tmRows);
        setRfis(rfiRows);
      })
      .catch((requestError) => {
        console.error("Failed to load project ticket summaries", requestError);
        setTmLogs([]);
        setRfis([]);
        setTicketsError(
          "T&M and RFI summaries could not be loaded. Projects still appear below."
        );
      })
      .finally(() => setTicketsLoading(false));
  }, []);

  useEffect(() => {
    loadProjects();
    loadTicketSummaries();
  }, [loadProjects, loadTicketSummaries]);

  const customerFilterOptions = useMemo(
    () => buildProjectCustomerFilterOptions(projects),
    [projects]
  );
  const managerFilterOptions = useMemo(
    () => buildProjectManagerFilterOptions(projects),
    [projects]
  );

  // Precompute per-project T&M / RFI card summaries once ticket lists load.
  const ticketSummariesByProjectId = useMemo(() => {
    const tmByProject = groupTicketsByProjectId(projects, tmLogs);
    const rfiByProject = groupTicketsByProjectId(projects, rfis);
    const summaries = new Map();
    for (const project of projects) {
      const key = String(project.id);
      summaries.set(key, {
        tm: buildTmCardSummary(tmByProject.get(key) || []),
        rfi: buildRfiCardSummary(rfiByProject.get(key) || []),
      });
    }
    return summaries;
  }, [projects, tmLogs, rfis]);

  const visibleProjects = useMemo(() => {
    // Prefer serializer ``active`` (is_job_date_window_active); fall back to dates.
    const scopedProjects = showCompletedProjects
      ? projects
      : projects.filter((project) =>
          typeof project.active === "boolean"
            ? project.active
            : isDayWithinJobActiveRange(getTodayInPT(), project)
        );
    const filtered = scopedProjects.filter(
      (project) =>
        projectMatchesDateRangeFilter(project, filterDateStart, filterDateEnd) &&
        projectMatchesCustomerFilter(project, filterCustomerIds) &&
        projectMatchesManagerFilter(project, filterManagerIds)
    );
    return filterProjects(filtered, searchQuery);
  }, [
    projects,
    searchQuery,
    showCompletedProjects,
    filterDateStart,
    filterDateEnd,
    filterCustomerIds,
    filterManagerIds,
  ]);

  // Apply the selected sort mode (and optional reverse) once summaries are ready.
  const displayedProjects = useMemo(
    () =>
      sortProjectsForHomeCards(
        visibleProjects,
        opsSummariesByProjectId,
        ticketSummariesByProjectId,
        { sortMode, reverseSort }
      ),
    [
      visibleProjects,
      opsSummariesByProjectId,
      ticketSummariesByProjectId,
      sortMode,
      reverseSort,
    ]
  );

  // Row-major columns so sorted order reads left → right, then top → bottom.
  const displayedProjectColumns = useMemo(
    () => distributeProjectsIntoColumns(displayedProjects, cardColumnCount),
    [displayedProjects, cardColumnCount]
  );

  // Card-view concrete/labor briefs (today + 7d); skipped in list view.
  useEffect(() => {
    if (!isCardView || visibleProjects.length === 0) {
      setOpsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setOpsSummariesByProjectId(new Map());
    setOpsLoading(true);
    loadOpsSummariesForProjects(visibleProjects, controller.signal)
      .then((summaries) => {
        if (!controller.signal.aborted) {
          setOpsSummariesByProjectId(summaries);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setOpsLoading(false);
        }
      });

    return () => controller.abort();
  }, [isCardView, visibleProjects]);

  const filterStateByKey = useMemo(
    () => ({
      showCompletedProjects: {
        checked: showCompletedProjects,
        onChange: setShowCompletedProjects,
      },
      showZeroMetrics: {
        checked: showZeroMetrics,
        onChange: setShowZeroMetrics,
      },
      useFlexGridLayout: {
        checked: useFlexGridLayout,
        onChange: setUseFlexGridLayout,
      },
    }),
    [showCompletedProjects, showZeroMetrics, useFlexGridLayout]
  );
  const projectFilterCheckboxes = useMemo(
    () =>
      PROJECTS_HOME_FILTERS.filter((filter) => filter.active).map((filter) => ({
        id: filter.id,
        label: filter.label,
        checked: filterStateByKey[filter.key].checked,
        onChange: filterStateByKey[filter.key].onChange,
      })),
    [filterStateByKey]
  );
  const projectFilterDateRanges = useMemo(
    () => [
      {
        id: PROJECTS_HOME_DATE_RANGE_FILTER.id,
        label: PROJECTS_HOME_DATE_RANGE_FILTER.label,
        startLabel: PROJECTS_HOME_DATE_RANGE_FILTER.startLabel,
        endLabel: PROJECTS_HOME_DATE_RANGE_FILTER.endLabel,
        startValue: filterDateStart,
        endValue: filterDateEnd,
        onStartChange: setFilterDateStart,
        onEndChange: setFilterDateEnd,
      },
    ],
    [filterDateStart, filterDateEnd]
  );
  const projectFilterSelects = useMemo(
    () => [
      {
        id: PROJECTS_HOME_MULTI_SELECT_FILTERS.customers.id,
        label: PROJECTS_HOME_MULTI_SELECT_FILTERS.customers.label,
        value: filterCustomerIds,
        options: customerFilterOptions,
        onChange: setFilterCustomerIds,
        placeholder: PROJECTS_HOME_MULTI_SELECT_FILTERS.customers.placeholder,
        emptyPlaceholder:
          PROJECTS_HOME_MULTI_SELECT_FILTERS.customers.emptyPlaceholder,
      },
      {
        id: PROJECTS_HOME_MULTI_SELECT_FILTERS.managers.id,
        label: PROJECTS_HOME_MULTI_SELECT_FILTERS.managers.label,
        value: filterManagerIds,
        options: managerFilterOptions,
        onChange: setFilterManagerIds,
        placeholder: PROJECTS_HOME_MULTI_SELECT_FILTERS.managers.placeholder,
        emptyPlaceholder:
          PROJECTS_HOME_MULTI_SELECT_FILTERS.managers.emptyPlaceholder,
      },
    ],
    [
      filterCustomerIds,
      filterManagerIds,
      customerFilterOptions,
      managerFilterOptions,
    ]
  );
  const projectFilterSingleSelects = useMemo(
    () => [
      {
        id: PROJECTS_HOME_SORT_FILTER.id,
        label: PROJECTS_HOME_SORT_FILTER.label,
        value: sortMode,
        options: PROJECTS_HOME_SORT_OPTIONS,
        onChange: setSortMode,
      },
    ],
    [sortMode]
  );
  const projectFilterFooterCheckboxes = useMemo(
    () => [
      {
        id: PROJECTS_HOME_SORT_FILTER.reverseId,
        label: PROJECTS_HOME_SORT_FILTER.reverseLabel,
        checked: reverseSort,
        onChange: setReverseSort,
      },
    ],
    [reverseSort]
  );
  const projectFiltersActive =
    showCompletedProjects !==
      getProjectHomeFilterDefault("showCompletedProjects") ||
    showZeroMetrics !== getProjectHomeFilterDefault("showZeroMetrics") ||
    useFlexGridLayout !==
      getProjectHomeFilterDefault("useFlexGridLayout") ||
    sortMode !== PROJECTS_HOME_SORT_DEFAULTS.mode ||
    reverseSort !== PROJECTS_HOME_SORT_DEFAULTS.reverse ||
    filterDateStart !== PROJECTS_HOME_DATE_RANGE_DEFAULTS.start ||
    filterDateEnd !== PROJECTS_HOME_DATE_RANGE_DEFAULTS.end ||
    filterCustomerIds.length > 0 ||
    filterManagerIds.length > 0;
  const isInitialLoading = isLoading && projects.length === 0;

  /**
   * Open the edit modal for a project from card or list surfaces.
   *
   * @param {Object} project - Project API row.
   * @returns {void}
   */
  const handleEditProject = (project) => {
    setIsAddingProject(false);
    setEditingProject(project);
  };

  /**
   * Render one project card with its ticket + ops summaries.
   * Shared by the flex-column and CSS-grid card layouts.
   *
   * @param {Object} project - Project API row.
   * @returns {JSX.Element}
   */
  const renderProjectCard = (project) => {
    const key = String(project.id);
    const summaries = ticketSummariesByProjectId.get(key);
    return (
      <ProjectCard
        key={project.id}
        project={project}
        onEdit={canManage ? handleEditProject : undefined}
        tmSummary={summaries?.tm || emptyTmCardSummary()}
        rfiSummary={summaries?.rfi || emptyRfiCardSummary()}
        ticketsLoading={ticketsLoading}
        opsSummary={
          opsSummariesByProjectId.get(key) || emptyProjectCardOpsSummary()
        }
        opsLoading={opsLoading && !opsSummariesByProjectId.has(key)}
        showZeroMetrics={showZeroMetrics}
      />
    );
  };

  if (isInitialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-100 font-inter text-gray-900">
      <header className="mb-2 w-full bg-gray-200">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6 lg:px-8">
          <div className="min-w-40">
            <h1 className="text-xl font-bold leading-none tracking-tight text-gray-950">
              Projects Home
            </h1>
            <p className="mt-1 flex items-center gap-2 text-xs font-medium text-gray-600">
              <span>
                Showing {visibleProjects.length} of {projects.length} projects
              </span>
              {isLoading ? (
                <span className="inline-flex items-center gap-1.5 font-medium text-primary">
                  <Spinner size="h-3.5 w-3.5" className="text-primary" />
                  Refreshing…
                </span>
              ) : null}
            </p>
          </div>

          <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={PROJECTS_HOME_SEARCH_PLACEHOLDER}
                className="h-8 w-full min-w-[220px] rounded-md border border-gray-300 bg-white pl-8 pr-2.5 text-xs text-gray-950 shadow-sm outline-none transition focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100"
              />
            </div>

            <ProjectsViewToggle viewMode={viewMode} onChange={setViewMode} />

            <FiltersPanel
              checkboxes={projectFilterCheckboxes}
              dateRanges={projectFilterDateRanges}
              selects={projectFilterSelects}
              singleSelects={projectFilterSingleSelects}
              footerCheckboxes={projectFilterFooterCheckboxes}
              isActive={projectFiltersActive}
              onClearAll={() => {
                setShowCompletedProjects(
                  getProjectHomeFilterDefault("showCompletedProjects")
                );
                setShowZeroMetrics(
                  getProjectHomeFilterDefault("showZeroMetrics")
                );
                setUseFlexGridLayout(
                  getProjectHomeFilterDefault("useFlexGridLayout")
                );
                setSortMode(PROJECTS_HOME_SORT_DEFAULTS.mode);
                setReverseSort(PROJECTS_HOME_SORT_DEFAULTS.reverse);
                setFilterDateStart(PROJECTS_HOME_DATE_RANGE_DEFAULTS.start);
                setFilterDateEnd(PROJECTS_HOME_DATE_RANGE_DEFAULTS.end);
                setFilterCustomerIds([]);
                setFilterManagerIds([]);
              }}
              ariaLabel="Project filters"
              variant="header"
            />

            {canManage ? (
              <button
                type="button"
                onClick={() => {
                  setEditingProject(null);
                  setIsAddingProject(true);
                }}
                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-300"
              >
                <Plus className="h-3.5 w-3.5" />
                {PROJECTS_HOME_ADD_PROJECT_LABEL}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 pb-3 sm:px-6 lg:px-8">
        {error ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            <span>{error}</span>
            <button
              type="button"
              onClick={loadProjects}
              className="rounded-md bg-red-700 px-3 py-1 text-xs font-semibold text-white hover:bg-red-800"
            >
              Retry
            </button>
          </div>
        ) : null}

        {ticketsError ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
            <span>{ticketsError}</span>
            <button
              type="button"
              onClick={loadTicketSummaries}
              className="rounded-md bg-amber-800 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-900"
            >
              Retry
            </button>
          </div>
        ) : null}

        {isCardView ? (
          useFlexGridLayout ? (
            <section
              className={`flex items-start gap-3 transition-opacity ${
                isLoading ? "opacity-70" : ""
              }`}
            >
              {displayedProjectColumns.map((columnProjects, columnIndex) => (
                <div
                  key={`project-card-column-${columnIndex}`}
                  className="flex min-w-0 flex-1 flex-col gap-3"
                >
                  {columnProjects.map(renderProjectCard)}
                </div>
              ))}
            </section>
          ) : (
            <section
              className={`grid items-start gap-3 transition-opacity md:grid-cols-2 xl:grid-cols-3 ${
                isLoading ? "opacity-70" : ""
              }`}
            >
              {displayedProjects.map(renderProjectCard)}
            </section>
          )
        ) : (
          <section className={`transition-opacity ${isLoading ? "opacity-70" : ""}`}>
            {displayedProjects.length ? (
              <ProjectList
                projects={displayedProjects}
                onEdit={canManage ? handleEditProject : undefined}
                ticketSummariesByProjectId={ticketSummariesByProjectId}
                ticketsLoading={ticketsLoading}
                showZeroMetrics={showZeroMetrics}
              />
            ) : null}
          </section>
        )}

        {!displayedProjects.length ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/90 p-8 text-center text-gray-600 shadow-sm">
            {projects.length === 0
              ? "No projects yet."
              : "No projects match the current filters."}
          </div>
        ) : null}
      </div>
      <ProjectFormModal
        open={isProjectModalOpen}
        mode={projectModalMode}
        project={editingProject}
        onSubmit={(payload) =>
          isAddingProject
            ? createProject(payload)
            : updateProject(editingProject.id, payload)
        }
        onClose={() => {
          setIsAddingProject(false);
          setEditingProject(null);
        }}
        onSaved={() => {
          setIsAddingProject(false);
          setEditingProject(null);
          loadProjects();
          loadTicketSummaries();
        }}
      />
    </main>
  );
};

export default ProjectsHome;
